import classNames from "classnames";
import { values } from "lodash";
import { useBookingModuleContext } from "PFApp/booking/parts/providers/booking_module_context_provider";
import { CalendarMode, CalendarView } from "PFApp/booking/types";
import FiltersBox from "PFApp/components/filters/filters_box";
import FiltersPreview from "PFApp/components/filters/filters_preview";
import { Button } from "PFComponents/button";
import ButtonSelect from "PFComponents/select/button_select";
import Toggle from "PFComponents/toggle/toggle";
import Tooltip from "PFComponents/tooltip/tooltip";
import { useDeterministicStringify } from "PFCore/helpers/use_deterministic_stringity";
import { useIsCurrentUserPermittedTo } from "PFCore/helpers/use_is_permitted_to";
import CogIcon from "PFIcons/cog.svg";
import { Meta, PermissionRule } from "PFTypes";
import { PageTarget } from "PFTypes/saved_filters";
import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { SavedViewsContextProvider } from "../../components/filters/saved_views_context/saved_views_context";
import { getAPIFilters, getValueFilters } from "../../use_filtered_collection";
import { EditMode } from "../types/edit_mode";
import { FiltersSearch } from "./filters_search";
import { FILTERS_ROW_HEIGHT } from "./overview/calendar/calendar";
import { ProjectsCalendarData, SettingsOptions } from "./overview/context/booking_overview_context.types";
import { useBookingRowsContext } from "./overview/context/booking_rows_context";
import { useBookingsOverviewContext } from "./overview/context/bookings_overview_context";
import { CreateBookingButton } from "./overview/create_booking_button";
import css from "./toolbar.module.scss";
import { ToolbarChildrenFiltersToggle } from "./toolbar_children_filters_toggle";
import { ViewAndMetricSelector } from "./view_and_metric_selector";

const mapFiltersMetaToDisplayHiddenFilters = (meta: Meta): Meta => {
  const hasIdFilter = !!meta.filters?.fields?.id?.value;
  const hasProfileIdFilter = !!meta.filters?.fields?.profile_id?.value;
  if (!hasIdFilter && !hasProfileIdFilter) {
    return meta;
  }
  return {
    ...meta,
    filters: {
      ...meta.filters,
      fields: {
        ...meta.filters!.fields,
        ...(hasIdFilter ? { id: { ...meta.filters!.fields!.id, type: "integer", hidden: false } } : {}),
        ...(hasProfileIdFilter
          ? { profile_id: { ...meta.filters!.fields!.profile_id, type: "integer", hidden: false } }
          : {})
      }
    }
  };
};

const getSavedFiltersTarget = (calendarMode: CalendarMode, calendarView: CalendarView) => {
  if (calendarMode === CalendarMode.Workforce) {
    return PageTarget.BookingModuleWorkforce;
  }
  switch (calendarView) {
    case CalendarView.EngagementsAndAuditEngagements:
      return PageTarget.BookingModuleProjectsEngagements;
    case CalendarView.RolesAndAuditRoles:
      return PageTarget.BookingModuleProjectsRoles;
    default:
      throw new Error("Invalid combination of view and mode!");
  }
};

export const Toolbar = () => {
  const isBookingResourcer = useIsCurrentUserPermittedTo(PermissionRule.BookingResourcer);

  const { t } = useTranslation(["translation", "bookingModule"]);
  const {
    calendarMode,
    workWeekEnabled,
    setWorkWeekEnabled,
    calendarData: { updateFilter, setFilters, resetFilters, noFiltersSelected, data, view }
  } = useBookingsOverviewContext();
  const {
    calendarData: { childrenFiltersEnabled }
  } = useBookingsOverviewContext<ProjectsCalendarData>();
  const { meta, loading } = data;
  const { setEditMode, editMode } = useBookingRowsContext();
  const { headerDisabled, setHeaderDisabled } = useBookingModuleContext();

  const showSettings = !!localStorage.getItem("bm_settings_enabled"); // TODO: [ENG-2276] temporary flag to hide the feature. Remove once Don't display weekends feature is ready.

  const stringify = useDeterministicStringify();
  const [showFilters, setShowFilters] = useState(false);

  const termFilter = meta.filters?.fields?.term;
  const handleTermChange = (term) => termFilter && updateFilter(termFilter, term);

  const termValue = termFilter?.value || "";
  const viewsKey = getSavedFiltersTarget(calendarMode, view);
  const portalRef = useRef(null);

  const settingsMap = useMemo(
    () => ({
      [SettingsOptions.WorkWeek]: {
        value: workWeekEnabled,
        action: setWorkWeekEnabled
      }
    }),
    [workWeekEnabled]
  );

  const settingsOptions = useMemo(
    () =>
      values(SettingsOptions).map((setting) => ({
        id: setting,
        displayElement: (
          <Toggle
            description={t(`bookingModule:settings.${setting}`)}
            onChange={(value) => settingsMap[setting].action(value)}
            checked={settingsMap[setting].value}
            reversed
            inline
          />
        ),
        item: setting
      })),
    [settingsMap]
  );

  const filtersPreviewMeta: Meta = useMemo(() => {
    if (calendarMode !== CalendarMode.Projects) {
      return meta;
    } else {
      return mapFiltersMetaToDisplayHiddenFilters(meta);
    }
  }, [meta, calendarMode]);

  const areFiltersClear = useMemo(
    () => noFiltersSelected && (!meta.filters?.fields?.id?.value || calendarMode !== CalendarMode.Projects),
    [noFiltersSelected, meta, calendarMode]
  );

  const filtersDisabled = useMemo(
    () => calendarMode === CalendarMode.Workforce && !isBookingResourcer,
    [calendarMode, isBookingResourcer]
  );

  return (
    <SavedViewsContextProvider viewsKey={viewsKey}>
      <div className={css.container} style={{ height: FILTERS_ROW_HEIGHT }}>
        {!showFilters && (
          <Tooltip content={filtersDisabled ? t("bookingModule:bookings.tooltip.searchDisabled") : ""}>
            <div>
              <FiltersSearch
                termValue={termValue as string}
                onTermChange={handleTermChange}
                disabled={headerDisabled || !!filtersDisabled}
              />
            </div>
          </Tooltip>
        )}
        <div className={css.filters}>
          <div ref={portalRef} />
          {!showFilters && (
            <>
              <div className={css.filterPreview}>
                {!filtersDisabled && (
                  <FiltersPreview
                    meta={filtersPreviewMeta}
                    style={{ flexGrow: 1 }}
                    blockedList={["term", "template_key"]}
                    blockedListChildren={["template_key"]}
                    filtersAreClear={areFiltersClear}
                    clearFilters={resetFilters}
                    onFilterChange={updateFilter}
                    onMoreClicked={() => setShowFilters(true)}
                    viewsKey={viewsKey}
                    disabled={headerDisabled || filtersDisabled}
                    filterSectionLabels={
                      view === CalendarView.EngagementsAndAuditEngagements
                        ? {
                            root: t("translation:engagement"),
                            children: t("translation:role")
                          }
                        : undefined
                    }
                  />
                )}
              </div>
              <div className={css.toolbarActions}>
                <Tooltip content={filtersDisabled ? t("bookingModule:bookings.tooltip.filtersDisabled") : ""}>
                  <div>
                    <Button
                      icon="filter"
                      kind="secondary"
                      className={classNames([css.toolbarIconButton, css.fullHeight])}
                      onClick={() => setShowFilters((prev) => !prev)}
                      disabled={headerDisabled || filtersDisabled}
                    />
                  </div>
                </Tooltip>
                <div>
                  <Button
                    icon="split"
                    kind={editMode === EditMode.Split ? "tertiary" : "secondary"}
                    className={classNames([css.toolbarIconButton, css.fullHeight])}
                    onClick={() => {
                      setEditMode(EditMode.Split);
                      setHeaderDisabled(true);
                    }}
                    disabled={headerDisabled}
                  />
                </div>
                {view === CalendarView.WallPlanner && (
                  <div>
                    <Button
                      icon="edit"
                      kind={editMode === EditMode.WallPlanner ? "tertiary" : "secondary"}
                      className={classNames([css.toolbarIconButton, css.fullHeight])}
                      onClick={() => {
                        setEditMode(EditMode.WallPlanner);
                        setHeaderDisabled(true);
                      }}
                      disabled={headerDisabled}
                    />
                  </div>
                )}
                {showSettings && (
                  <ButtonSelect
                    kind="secondary"
                    onChange={() => {}}
                    buttonClassName={classNames([css.toolbarIconButton, css.fullHeight])}
                    dropDownClassName={css.dropdownRoot}
                    preserveDropdown
                    options={settingsOptions}
                    title={<CogIcon width={16} height={16} />}
                    disabled={headerDisabled}
                  />
                )}
                <div>
                  <CreateBookingButton />
                </div>
                <ViewAndMetricSelector />
              </div>
            </>
          )}
          {showFilters && (
            <div className={css.filtersBox}>
              <FiltersBox
                style={{
                  position: "relative",
                  width: "100%"
                }}
                classes={{
                  list: css.filtersBoxList
                }}
                blockedList={[]}
                isLoading={loading}
                meta={meta}
                handleFilterChange={updateFilter}
                handleFiltersChange={setFilters}
                clearFilters={resetFilters}
                filtersAreClear={areFiltersClear}
                closeFilters={() => setShowFilters(false)}
                clearRefValue={stringify(getAPIFilters(getValueFilters(meta.filters)))}
                allowToClose
                viewsKey={viewsKey}
                portalRef={portalRef}
                accordionProps={
                  view === CalendarView.EngagementsAndAuditEngagements
                    ? {
                        childrenTitle: t("bookingModule:filters.engagementsAndAuditEngagements"),
                        filtersTitle: <ToolbarChildrenFiltersToggle viewsKey={viewsKey} />,
                        filtersDisabled: !childrenFiltersEnabled,
                        activeState: { 0: true, 1: childrenFiltersEnabled },
                        allowMultipleActive: true
                      }
                    : undefined
                }
              />
            </div>
          )}
        </div>
      </div>
    </SavedViewsContextProvider>
  );
};
